import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tabs } from 'antd';
import { Pane, Loader, Button, Tooltip } from 'components/common';
import { useGet, usePost, useAuth } from 'hooks';

import Unauthed from './Unauthed';
import Table from './Table';
import Modal from './Modal';
import DynamicsConnectModal from './DynamicsConnectModal';
import ConnectionsOverview from './ConnectionsOverview';
import {
  getCrmSaveUrl,
  getCrmDataUrl,
  getCrmDisconnectUrl,
  getCrmImage,
} from './helpers';
import { crmTypes } from 'utils/helpers/crm';
import { reloadCrmConnections } from 'actions/other';
import { getReloadCrmConnections } from '../../../selectors/other';
import { AuthPanel } from './styled';

const Actions = ({ mappings, fetchData, crmType }) => {
  const [{ res, loading }, saveChanges] = usePost({
    url: getCrmSaveUrl(crmType),
  });
  const [{ res: disconnectData }, disconnect] = usePost({
    url: getCrmDisconnectUrl(crmType),
  });

  const { checkUser, user } = useAuth();

  useEffect(() => {
    if (res || disconnectData) {
      fetchData();
      checkUser();
    }
  }, [res, disconnectData, fetchData, checkUser]);

  const handleSave = () => {
    saveChanges({
      mappings,
      crmType: crmType,
    });
  };

  return (
    <div>
      <Tooltip
        title={`This will disconnect your personal account from ${crmType}`}
      >
        <Button
          size='small'
          type='danger'
          ghost
          style={{ marginRight: 10 }}
          onClick={() => disconnect()}
        >
          Disconnect
        </Button>
      </Tooltip>
      {user.isAccountManager && (
        <Tooltip title='These mapping changes will be applied to all users on this account'>
          <Button
            size='small'
            type='primary'
            disabled={loading}
            onClick={handleSave}
          >
            Save Changes
          </Button>
        </Tooltip>
      )}
    </div>
  );
};

const CRMIntegration = ({ crmType }) => {
  const dispatch = useDispatch();
  const [{ res, loading }, fetchData] = useGet({
    url: getCrmDataUrl(crmType),
    onMount: true,
  });
  const [field, setField] = useState();
  const [mappings, setMappings] = useState();
  const [showDynamicsModal, setShowDynamicsModal] = useState(false);
  const reload = useSelector(getReloadCrmConnections) || false;

  useEffect(() => {
    res && setMappings(res.mappings);
  }, [res]);

  useEffect(() => {
    if (reload) {
      fetchData();
      dispatch(reloadCrmConnections(false));
    }
  }, [reload, dispatch, fetchData]);

  const updateMapping = (expression, { mapping, sfField }) => {
    let current = mappings;
    if (mapping.expression === expression) {
      return setField();
    }
    const existing = current.find(m => m.crmField === mapping.crmField);
    if (existing) {
      if (expression) {
        existing.expression = expression;
      } else {
        current = current.filter(c => c.crmField !== mapping.crmField);
      }
    } else {
      current.push({
        crmType: crmType,
        crmField: sfField.name,
        crmFieldType: sfField.type,
        crmFieldDisplayName: sfField.displayName,
        expression,
        isEnabled: true,
      });
    }

    setMappings([...current]);
    setField();
  };

  const toggleMappingStatus = ({ mapping, sfField }) => {
    let current = mappings;

    const existing = current.find(m => m.crmField === mapping.crmField);
    if (existing) {
      if (existing.expression) {
        existing.isEnabled = !existing.isEnabled;
      } else {
        current = current.filter(c => c.crmField !== mapping.crmField);
      }
    } else {
      current.push({
        crmType: crmType,
        crmField: sfField.name,
        crmFieldType: sfField.type,
        crmFieldDisplayName: sfField.displayName,
        isEnabled: false,
      });
    }

    setMappings([...current]);
    setField();
  };

  if (loading) {
    return <Loader />;
  }

  // State 1: Dynamics-specific - Show modal only when crmAuthUrl is null (no serviceUrl stored)
  if (
    crmType === crmTypes.DYNAMICS &&
    res &&
    !res.isAuthenticated &&
    !res.crmAuthUrl
  ) {
    return (
      <>
        <Pane title={crmType}>
          <AuthPanel>
            <img alt={crmType + ' logo'} src={getCrmImage(crmType)} />
            <Button type='primary' onClick={() => setShowDynamicsModal(true)}>
              Connect Dynamics 365
            </Button>
          </AuthPanel>
        </Pane>
        {showDynamicsModal && (
          <DynamicsConnectModal
            onClose={() => setShowDynamicsModal(false)}
            onConnected={fetchData}
          />
        )}
      </>
    );
  }

  // State 2: All CRMs (including Dynamics) - Show Unauthed when crmAuthUrl exists but not authenticated
  if (res && !res.isAuthenticated && res.crmAuthUrl) {
    return <Unauthed url={res.crmAuthUrl} crmType={crmType} />;
  }

  return (
    <Pane
      title={crmType}
      headerRight={
        <Actions mappings={mappings} fetchData={fetchData} crmType={crmType} />
      }
    >
      {crmType === crmTypes.DYNAMICS && res?.instanceUrl && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ color: '#666' }}>{res.instanceUrl}</div>
          <Button
            size='small'
            type='link'
            onClick={() => setShowDynamicsModal(true)}
            style={{ padding: 0, marginTop: 4 }}
          >
            Edit Instance URL
          </Button>
        </div>
      )}
      <Table
        data={res}
        mappings={mappings}
        setField={setField}
        researcherFields={res.researcherFields}
        clearRow={r => updateMapping('', r)}
        toggleEnabled={r => toggleMappingStatus(r)}
      />
      {field && (
        <Modal
          field={field}
          setField={setField}
          onOk={updateMapping}
          researcherFields={res.researcherFields}
        />
      )}
      {crmType === crmTypes.DYNAMICS && showDynamicsModal && (
        <DynamicsConnectModal
          onClose={() => setShowDynamicsModal(false)}
          onConnected={fetchData}
          initialServiceUrl={res?.instanceUrl}
        />
      )}
    </Pane>
  );
};

// Component to track CRM authentication status
const CrmStatusTracker = ({ crmType, onStatusChange, reload }) => {
  const [{ res }, fetchData] = useGet({
    url: getCrmDataUrl(crmType),
    onMount: true,
  });

  useEffect(() => {
    if (reload) {
      fetchData();
    }
  }, [reload, fetchData]);

  useEffect(() => {
    if (onStatusChange && res) {
      onStatusChange(crmType, res.isAuthenticated || false);
    }
  }, [res, crmType, onStatusChange]);

  return null;
};

const CRMIntegrations = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('connections');
  const [connectedCrms, setConnectedCrms] = useState([]);
  const dispatch = useDispatch();
  const reload = useSelector(getReloadCrmConnections) || false;
  const prevConnectedCrmsRef = useRef([]);

  // Determine available CRMs based on user permissions
  const availableCrms = useMemo(() => {
    const crms = [];
    if (user.canPushToSalesforce) crms.push(crmTypes.SALESFORCE);
    if (user.canPushToHubspot) crms.push(crmTypes.HUBSPOT);
    if (user.canPushToDynamics) crms.push(crmTypes.DYNAMICS);
    return crms;
  }, [user]);

  const handleStatusChange = (crmType, isAuthenticated) => {
    setConnectedCrms(prev => {
      const exists = prev.includes(crmType);
      if (isAuthenticated && !exists) {
        return [...prev, crmType];
      } else if (!isAuthenticated && exists) {
        return prev.filter(c => c !== crmType);
      }
      return prev;
    });
  };

  useEffect(() => {
    if (reload) {
      dispatch(reloadCrmConnections(false));
      // Reset status tracking to refetch
      setConnectedCrms([]);
    }
  }, [reload, dispatch]);

  const handleTabSwitch = useCallback(crmType => {
    setActiveTab(crmType.toLowerCase());
  }, []);

  const handleDisconnect = useCallback(() => {
    setActiveTab('connections');
  }, []);

  // Build tab items
  const tabItems = useMemo(() => {
    const connectionsTab = {
      label: connectedCrms.length > 0 ? 'Add more Integrations' : 'Connections',
      key: 'connections',
      children: (
        <ConnectionsOverview
          availableCrms={availableCrms}
          onTabSwitch={handleTabSwitch}
          onDisconnect={handleDisconnect}
        />
      ),
    };
    const items = [];

    // Add tabs for connected CRMs
    connectedCrms.forEach(crmType => {
      items.push({
        label: crmType,
        key: crmType.toLowerCase(),
        children: <CRMIntegration crmType={crmType} />,
      });
    });

    if (connectedCrms.length === 1) {
      items.push(connectionsTab);
    } else {
      items.unshift(connectionsTab);
    }

    return items;
  }, [availableCrms, connectedCrms, handleTabSwitch, handleDisconnect]);

  // Set default active tab and handle disconnections
  useEffect(() => {
    const currentTabCrm = connectedCrms.find(
      c => c.toLowerCase() === activeTab
    );
    const isConnectionsTab = activeTab === 'connections';
    const prevConnectedCrms = prevConnectedCrmsRef.current;

    // Detect if a new CRM was just connected
    const newlyConnectedCrm = connectedCrms.find(
      crm => !prevConnectedCrms.includes(crm)
    );

    if (newlyConnectedCrm && isConnectionsTab) {
      // Switch to the newly connected CRM
      setActiveTab(newlyConnectedCrm.toLowerCase());
    } else if (connectedCrms.length === 0 && !isConnectionsTab) {
      // No CRMs connected, switch to connections
      setActiveTab('connections');
    } else if (
      connectedCrms.length > 0 &&
      !currentTabCrm &&
      !isConnectionsTab
    ) {
      // Current tab's CRM was disconnected, switch to connections
      setActiveTab('connections');
    }

    prevConnectedCrmsRef.current = [...connectedCrms];
  }, [connectedCrms, activeTab]);

  return (
    <Pane title='CRM Integrations'>
      {availableCrms.map(crmType => (
        <CrmStatusTracker
          key={crmType}
          crmType={crmType}
          onStatusChange={handleStatusChange}
          reload={reload}
        />
      ))}
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
    </Pane>
  );
};

export default CRMIntegrations;
